// ===================================
// RESET PASSWORD PAGE
// frontend/src/pages/ResetPassword.js
// Set new password using reset token from email
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ResetPassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  // Get token and email from URL
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token || !email) {
        setVerifying(false);
        setTokenValid(false);
        setError('Invalid reset link. Please request a new one.');
        return;
      }

      try {
        const response = await api.post('/auth/verify-reset-token', { token, email });
        
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setError(response.data.message || 'Invalid or expired reset link');
        }
      } catch (err) {
        setError('Failed to verify reset link. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, email]);

  // Password strength indicator
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength++;

    const levels = [
      { label: 'Very Weak', color: '#ef4444' },
      { label: 'Weak', color: '#f97316' },
      { label: 'Fair', color: '#eab308' },
      { label: 'Good', color: '#22c55e' },
      { label: 'Strong', color: '#10b981' }
    ];

    const level = Math.min(strength, 4);
    return { strength: level + 1, ...levels[level] };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        newPassword: password
      });

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.expired) {
        setError('Reset link has expired. Please request a new one.');
        setTokenValid(false);
      } else if (errorData?.authProvider === 'google') {
        setError('This account uses Google sign-in. Password cannot be reset.');
      } else {
        setError(errorData?.message || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verifying state
  if (verifying) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-logo">
            <Link to="/">
              <span className="logo-icon">🎯</span>
              <span className="logo-text">Career-Coach.AI</span>
            </Link>
          </div>
          <div className="verifying-state">
            <div className="spinner-large"></div>
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        {/* Logo */}
        <div className="reset-password-logo">
          <Link to="/">
            <span className="logo-icon">🎯</span>
            <span className="logo-text">Career-Coach.AI</span>
          </Link>
        </div>

        {/* Invalid Token State */}
        {!tokenValid && !success && (
          <>
            <div className="status-icon error">✗</div>
            <h1>Link Invalid</h1>
            <p className="subtitle error-text">{error}</p>
            
            <div className="action-buttons">
              <Link to="/forgot-password" className="btn btn-primary btn-block">
                Request New Reset Link
              </Link>
              <Link to="/login" className="btn btn-secondary btn-block">
                Back to Sign In
              </Link>
            </div>
          </>
        )}

        {/* Reset Form */}
        {tokenValid && !success && (
          <>
            <div className="reset-password-icon">🔑</div>
            <h1>Create new password</h1>
            <p className="subtitle">
              Enter a new password for<br />
              <strong>{email}</strong>
            </p>

            {/* Error Message */}
            {error && (
              <div className="reset-password-error">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="reset-password-form">
              {/* New Password */}
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    disabled={loading}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                
                {/* Password Strength Meter */}
                {password && (
                  <div className="password-strength">
                    <div className="strength-bar">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className={`strength-segment ${level <= passwordStrength.strength ? 'active' : ''}`}
                          style={{ backgroundColor: level <= passwordStrength.strength ? passwordStrength.color : '' }}
                        />
                      ))}
                    </div>
                    <span className="strength-label" style={{ color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  required
                  disabled={loading}
                />
                {confirmPassword && password !== confirmPassword && (
                  <span className="field-hint error">Passwords don't match</span>
                )}
                {confirmPassword && password === confirmPassword && password.length >= 8 && (
                  <span className="field-hint success">✓ Passwords match</span>
                )}
              </div>

              {/* Password Requirements */}
              <div className="password-requirements">
                <p className="requirements-title">Password must have:</p>
                <ul>
                  <li className={password.length >= 8 ? 'met' : ''}>
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'met' : ''}>
                    Upper and lowercase letters
                  </li>
                  <li className={/\d/.test(password) ? 'met' : ''}>
                    At least one number
                  </li>
                </ul>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-block" 
                disabled={loading || password.length < 8 || password !== confirmPassword}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </>
        )}

        {/* Success State */}
        {success && (
          <>
            <div className="status-icon success">✓</div>
            <h1>Password Reset!</h1>
            <p className="subtitle">
              Your password has been successfully changed. You can now sign in with your new password.
            </p>
            
            <Link to="/login" className="btn btn-primary btn-block">
              Sign In Now →
            </Link>
          </>
        )}

        {/* Back Link */}
        {!success && tokenValid && (
          <div className="back-to-login">
            <Link to="/login">← Back to Sign In</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;